import { useContext, useEffect, useState } from 'react';
import { BoundingBox, DisaggregatedMetric, GeoDetails, GeoTag } from '../services/types';
import MetricCategoriesContext from '../context/MetricCategoriesContext';
import { useOrgContext } from '../context/OrgIdContext';
import MetadataContext from '../context/MetadataContext';
import { indicatorsEnv } from '../utils/environment';
import { toCanonicalLod } from '../utils/lod';

const useFetchData = (currentTab: string) => {
    const { orgId } = useOrgContext()
    const { metricCategories } = useContext(MetricCategoriesContext);
    const { metadata } = useContext(MetadataContext);

    const [indicators, setIndicators] = useState<DisaggregatedMetric[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [dashboardView, setDashboardView] = useState('');
    const [geoDetails, setGeoDetails] = useState<GeoDetails[]>([]);
    const [geoLod, setGeoLod] = useState('');
    const [boundingBox, setBoundingBox] = useState<BoundingBox | null>(null);
    const [geoTag, setGeoTag] = useState<GeoTag | null>(null);
    const [lodMissing, setLodMissing] = useState<boolean>(false);

    const geoEncoding = 'delta';
    const isIframe = window.self !== window.top;
    const indicatorsUrl = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getallindicatorsv2`;

    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            console.log('Received message:', event.data);

            if (event.data.message === 'update') {
                const { params } = event.data;

                if (params) {
                    const rawGeoDetails: GeoDetails[] = params.area_view.geo_details || [];

                    // Default to raw geoDetails in case metadata isn't loaded yet
                    let filteredGeoDetails = rawGeoDetails;

                    // Only filter geoDetails if metadata is available and not empty
                    if (metadata && Array.isArray(metadata) && metadata.length > 0) {
                        const availableLods = new Set(
                            metadata
                            .flatMap(m => m.versions || [])
                            .map(v => toCanonicalLod(v.level_of_detail))
                            .filter(Boolean) as string[]
                        );

                        filteredGeoDetails = rawGeoDetails
                            .map(d => ({ ...d, lod: toCanonicalLod(d.lod) || d.lod }))
                            .filter(d => availableLods.has(toCanonicalLod(d.lod)!));
                    }
                    
                    // normalize IDs to strings (avoids later hiccups)
                    filteredGeoDetails = filteredGeoDetails.map(d => ({
                    ...d,
                    lod: toCanonicalLod(d.lod) || d.lod,
                    ids: (d.ids ?? []).map(String).filter(Boolean),
                    }));

                    // Set state with filtered (or unfiltered) geoDetails
                    setGeoDetails(filteredGeoDetails);

                    setGeoLod(params.area_view.geo_lod || '');
                    setDashboardView(params.dashboard_view || '');
                    setBoundingBox(params.area_view?.bounding_box || null);
                    setGeoTag(params.area_view.geo_tag);
                    const nextLodMissing = params.area_view?.lod_missing ?? false;

                    console.log("Received lod_missing:", nextLodMissing);
                    setLodMissing(nextLodMissing);

                    // Fetch layer id
                    console.log("orgId local: ", params.organization_id);
                }
            }
        };

        if (isIframe) {
            window.addEventListener("message", handleMessage);
        } else {
            // Ensure the dashboardView is synced with currentTab
            setDashboardView(currentTab?.toLowerCase());
        }

        return () => {
            if (isIframe) window.removeEventListener('message', handleMessage);
        };
    }, [currentTab, metadata, isIframe]);

    useEffect(() => {
        console.log("lodMissing state changed:", lodMissing);
    }, [lodMissing]);

    useEffect(() => {
        const fetchData = async () => {
            console.log('details:', geoDetails, geoLod, orgId, dashboardView, geoTag);

            if (lodMissing) {
                console.log('Current map selection is missing information in geoDetails. Skipping query and displaying clear selection message...');
                setLoading(false);
                return;
            }

            if (!dashboardView) {
                console.log('Current tab is not selected. Skipping fetch...');
                setLoading(false);
                return;
            }

            // Check if dashboardView (in lowercase) is in metricCategories (also converted to lowercase)
            const lowercaseMetricCategories = metricCategories.map(category => category.toLowerCase());
            if (!lowercaseMetricCategories.includes(dashboardView.toLowerCase())) {
                console.log('dashboardView is not in metricCategories. Skipping fetch...');
                return;
            }
            
            try {
                if (orgId !== null) {
                    setLoading(true);
                    // Fetch indicators data for current view
                    const indicatorsResponse = await fetch(indicatorsUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ geoDetails, orgId, dashboardView, geoEncoding, geoTag, allowDefault: false, environment: indicatorsEnv }),
                    });

                    if (!indicatorsResponse.ok) {
                        throw new Error('Network response was not ok');
                    }

                    const indicatorsResult = await indicatorsResponse.json();
                    console.log('indicators result:', indicatorsResult);
                    setIndicators(indicatorsResult);
                }
            } catch (error: any) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [geoDetails, geoLod, orgId, dashboardView, boundingBox, geoTag, metricCategories, lodMissing]);

    return { indicators, loading, error, dashboardView, geoDetails, geoTag, lodMissing };
};

export default useFetchData;

// small area in seattle for testing
// const geoDetails = [
//     {
//         "lod": "tract",
//         "ids": [
//             "53033003400",
//             "53033004701",
//             "53033003202",
//             "53033004800",
//             "53033004702",
//             "53033003201",
//             "53033004703"
//         ]
//     },
//     {
//         "lod": "county",
//         "ids": [
//             "53033"
//         ]
//     },
//     {
//         "lod": "school",
//         "ids": [
//             "5307710"
//         ]
//     }
// ]

// Washington State
// const geoTag = {
//     "type": "state",
//     "value": [
//         "53"
//     ]
// };


// King County
// const geoDetails = [
//     {
//         "lod": "block group",
//         "ids": [
//             "530330007004",
//             "530330008001",
//             "530330008002",
//             "530330009001",
//             "530330009002",
//             "530330010001",
//             "530330010002",
//             "530330022001",
//             "530330022002",
//             "530330022003",
//             "530330022004",
//             "530330024001",
//             "530330024002",
//             "530330039001",
//             "530330039002",
//             "530330040001",
//             "530330040002",
//             "530330041011",
//             "530330041012",
//             "530330041013",
//             "530330041021",
//             "530330041022",
//             "530330041023",
//             "530330042011",
//             "530330042012",
//             "530330042013",
//             "530330042021",
//             "530330042022",
//             "530330042023",
//             "530330043012",
//             "530330063001",
//             "530330063003",
//             "530330063004",
//             "530330043011",
//             "530330043013",
//             "530330043021",
//             "530330043022",
//             "530330043023",
//             "530330044011",
//             "530330044013",
//             "530330044021",
//             "530330044022",
//             "530330044023",
//             "530330053032",
//             "530330053051",
//             "530330053061",
//             "530330053071",
//             "530330053072",
//             "530330053073",
//             "530330062001",
//             "530330062002",
//             "530330063002",
//             "530330064001",
//             "530330045001",
//             "530330045002",
//             "530330052012",
//             "530330052013",
//             "530330052021",
//             "530330052022",
//             "530330052023",
//             "530330053031",
//             "530330053041",
//             "530330053042",
//             "530330053052",
//             "530330053062",
//             "530330061002",
//             "530330062003",
//             "530330064002",
//             "530330064003",
//             "530330076001",
//             "530330077001",
//             "530330078001",
//             "530330078002",
//             "530330078003",
//             "530330078004",
//             "530330089001",
//             "530330076002",
//             "530330077002",
//             "530330077003",
//             "530330077004",
//             "530330079022",
//             "530330088001",
//             "530330088002",
//             "530330088003",
//             "530330089002",
//             "530330089003",
//             "530330095001",
//             "530330095003",
//             "530330101023",
//             "530330101021",
//             "530330102001",
//             "530330102002",
//             "530330102003",
//             "530330102004",
//             "530330111021",
//             "530330118022",
//             "530330119021",
//             "530330119023",
//             "530330101022",
//             "530330103011",
//             "530330111022",
//             "530330111024",
//             "530330118011",
//             "530330118021",
//             "530330119011",
//             "530330004041",
//             "530330004042",
//             "530330001011",
//             "530330001012",
//             "530330001013",
//             "530330001021",
//             "530330001022",
//             "530330001023",
//             "530330002011",
//             "530330002012",
//             "530330002013",
//             "530330002021",
//             "530330002022",
//             "530330002023",
//             "530330006011",
//             "530330006013",
//             "530330007001",
//             "530330007002",
//             "530330007003",
//             "530330011001",
//             "530330011002",
//             "530330012011",
//             "530330012021",
//             "530330012022",
//             "530330012023",
//             "530330020001",
//             "530330020002",
//             "530330020003",
//             "530330021001",
//             "530330021002",
//             "530330021003",
//             "530330025001",
//             "530330025002",
//             "530330026001",
//             "530330026002",
//             "530330026003",
//             "530330026004",
//             "530330038001",
//             "530330038002",
//             "530330012013",
//             "530330013001",
//             "530330018001",
//             "530330019001",
//             "530330019002",
//             "530330019003",
//             "530330019004",
//             "530330027001",
//             "530330027002",
//             "530330027003",
//             "530330036011",
//             "530330036012",
//             "530330036013",
//             "530330036021",
//             "530330036022",
//             "530330036023",
//             "530330044012",
//             "530330003001",
//             "530330003002",
//             "530330004022",
//             "530330004032",
//             "530330004043",
//             "530330006012",
//             "530330006021",
//             "530330006022",
//             "530330006023",
//             "530330012012",
//             "530330046001",
//             "530330032012",
//             "530330056001",
//             "530330056002",
//             "530330056003",
//             "530330056004",
//             "530330056005",
//             "530330057001",
//             "530330057002",
//             "530330057003",
//             "530330057004",
//             "530330057005",
//             "530330058032",
//             "530330058041",
//             "530330058042",
//             "530330059012",
//             "530330059021",
//             "530330059022",
//             "530330059023",
//             "530330068002",
//             "530330068003",
//             "530330069001",
//             "530330069002",
//             "530330069003",
//             "530330070011",
//             "530330070012",
//             "530330070013",
//             "530330070021",
//             "530330071011",
//             "530330071012",
//             "530330071021",
//             "530330071022",
//             "530330072011",
//             "530330072012",
//             "530330072013",
//             "530330072021",
//             "530330072022",
//             "530330072023",
//             "530330073011",
//             "530330073021",
//             "530330073022",
//             "530330073023",
//             "530330073032",
//             "530330074032",
//             "530330074051",
//             "530330074052",
//             "530330074061",
//             "530330074062",
//             "530330075011",
//             "530330075012",
//             "530330075013",
//             "530330075021",
//             "530330075022",
//             "530330075023",
//             "530330075031",
//             "530330075032",
//             "530330079012",
//             "530330080021",
//             "530330080022",
//             "530330080023",
//             "530330080031",
//             "530330080032",
//             "530330080033",
//             "530330080041",
//             "530330080042",
//             "530330080043",
//             "530330081011",
//             "530330081012",
//             "530330081013",
//             "530330081021",
//             "530330081022",
//             "530330082001",
//             "530330082002",
//             "530330082003",
//             "530330083001",
//             "530330083002",
//             "530330084011",
//             "530330084012",
//             "530330084013",
//             "530330084021",
//             "530330084022",
//             "530330084023",
//             "530330085001",
//             "530330085002",
//             "530330085003",
//             "530330086001",
//             "530330086002",
//             "530330086003",
//             "530330086004",
//             "530330087001",
//             "530330087003",
//             "530330090001",
//             "530330090002",
//             "530330090003",
//             "530330091001",
//             "530330091002",
//             "530330092001",
//             "530330092002",
//             "530330093001",
//             "530330093002",
//             "530330093003",
//             "530330094001",
//             "530330094002",
//             "530330094003",
//             "530330094004",
//             "530330094005",
//             "530330096001",
//             "530330096002",
//             "530330096003",
//             "530330096004",
//             "530330097011",
//             "530330097012",
//             "530330097013",
//             "530330097014",
//             "530330097021",
//             "530330097022",
//             "530330097023",
//             "530330097024",
//             "530330098011",
//             "530330098012",
//             "530330098013",
//             "530330098021",
//             "530330098022",
//             "530330098023",
//             "530330099001",
//             "530330099002",
//             "530330099003",
//             "530330099004",
//             "530330100011",
//             "530330100012",
//             "530330100013",
//             "530330100021",
//             "530330100022",
//             "530330100023",
//             "530330101011",
//             "530330101012",
//             "530330103021",
//             "530330103022",
//             "530330104011",
//             "530330104012",
//             "530330104013",
//             "530330104021",
//             "530330104022",
//             "530330104023",
//             "530330104024",
//             "530330105011",
//             "530330105012",
//             "530330105013",
//             "530330105021",
//             "530330105022",
//             "530330105023",
//             "530330105024",
//             "530330106011",
//             "530330106012",
//             "530330106013",
//             "530330106021",
//             "530330106022",
//             "530330106023",
//             "530330107011",
//             "530330107012",
//             "530330107013",
//             "530330107021",
//             "530330107022",
//             "530330107023",
//             "530330108001",
//             "530330108002",
//             "530330108003",
//             "530330109001",
//             "530330109002",
//             "530330110011",
//             "530330110012",
//             "530330110013",
//             "530330110021",
//             "530330110022",
//             "530330110023",
//             "530330112001",
//             "530330112002",
//             "530330112003",
//             "530330113001",
//             "530330113002",
//             "530330113003",
//             "530330113004",
//             "530330113005",
//             "530330114011",
//             "530330114012",
//             "530330114013",
//             "530330114021",
//             "530330114022",
//             "530330114023",
//             "530330115001",
//             "530330115002",
//             "530330115003",
//             "530330116011",
//             "530330116012",
//             "530330116021",
//             "530330116022",
//             "530330116023",
//             "530330117001",
//             "530330117002",
//             "530330117003",
//             "530330117004",
//             "530330119013",
//             "530330120001",
//             "530330120002",
//             "530330120003",
//             "530330121001",
//             "530330121002",
//             "530330265001",
//             "530330004021",
//             "530330004023",
//             "530330004024",
//             "530330004031",
//             "530330005001",
//             "530330005002",
//             "530330013002",
//             "530330013003",
//             "530330014001",
//             "530330014002",
//             "530330014003",
//             "530330014004",
//             "530330015001",
//             "530330015002",
//             "530330016001",
//             "530330016002",
//             "530330016003",
//             "530330017011",
//             "530330017012",
//             "530330017013",
//             "530330017021",
//             "530330017022",
//             "530330017023",
//             "530330017024",
//             "530330018002",
//             "530330018003",
//             "530330027004",
//             "530330028001",
//             "530330028002",
//             "530330028003",
//             "530330028004",
//             "530330029001",
//             "530330029002",
//             "530330029003",
//             "530330030001",
//             "530330030002",
//             "530330030003",
//             "530330030004",
//             "530330031001",
//             "530330031002",
//             "530330031003",
//             "530330031004",
//             "530330031005",
//             "530330032011",
//             "530330032013",
//             "530330032021",
//             "530330032022",
//             "530330032023",
//             "530330033011",
//             "530330033012",
//             "530330033013",
//             "530330033021",
//             "530330033022",
//             "530330033023",
//             "530330034001",
//             "530330034002",
//             "530330034003",
//             "530330035001",
//             "530330035002",
//             "530330035003",
//             "530330046002",
//             "530330046003",
//             "530330047011",
//             "530330047012",
//             "530330047013",
//             "530330047021",
//             "530330047022",
//             "530330047023",
//             "530330047031",
//             "530330047032",
//             "530330047033",
//             "530330048001",
//             "530330048002",
//             "530330048003",
//             "530330048004",
//             "530330049011",
//             "530330049012",
//             "530330049013",
//             "530330049021",
//             "530330049022",
//             "530330050001",
//             "530330050002",
//             "530330050003",
//             "530330051001",
//             "530330051002",
//             "530330051003",
//             "530330052011",
//             "530330054011",
//             "530330054012",
//             "530330054013",
//             "530330054021",
//             "530330054022",
//             "530330054023",
//             "530330058011",
//             "530330058012",
//             "530330058013",
//             "530330058014",
//             "530330058031",
//             "530330059011",
//             "530330059013",
//             "530330060001",
//             "530330060002",
//             "530330060003",
//             "530330060004",
//             "530330061001",
//             "530330061003",
//             "530330061004",
//             "530330061005",
//             "530330065001",
//             "530330065002",
//             "530330065003",
//             "530330066001",
//             "530330066002",
//             "530330066003",
//             "530330067011",
//             "530330067012",
//             "530330067013",
//             "530330067021",
//             "530330067022",
//             "530330067023",
//             "530330067031",
//             "530330067032",
//             "530330067033",
//             "530330068001",
//             "530330070022",
//             "530330070023",
//             "530330072031",
//             "530330072032",
//             "530330073012",
//             "530330073013",
//             "530330073031",
//             "530330073033",
//             "530330074031",
//             "530330074041",
//             "530330074042",
//             "530330076003",
//             "530330079011",
//             "530330079021",
//             "530330079023",
//             "530330087002",
//             "530330089004",
//             "530330095002",
//             "530330095004",
//             "530330101013",
//             "530330103012",
//             "530330103013",
//             "530330103023",
//             "530330111011",
//             "530330111012",
//             "530330111013",
//             "530330111014",
//             "530330111023",
//             "530330118012",
//             "530330118013",
//             "530330118023",
//             "530330119012"
//         ]
//     },
//     {
//         "lod": "tract",
//         "ids": [
//             "53033000700",
//             "53033000800",
//             "53033000900",
//             "53033001000",
//             "53033002200",
//             "53033002400",
//             "53033003900",
//             "53033004000",
//             "53033004101",
//             "53033004102",
//             "53033004201",
//             "53033004202",
//             "53033004301",
//             "53033006300",
//             "53033004302",
//             "53033004401",
//             "53033004402",
//             "53033005303",
//             "53033005305",
//             "53033005306",
//             "53033005307",
//             "53033006200",
//             "53033006400",
//             "53033004500",
//             "53033005201",
//             "53033005202",
//             "53033005304",
//             "53033006100",
//             "53033007600",
//             "53033007700",
//             "53033007800",
//             "53033008900",
//             "53033007902",
//             "53033008800",
//             "53033009500",
//             "53033010102",
//             "53033010200",
//             "53033011102",
//             "53033011802",
//             "53033011902",
//             "53033010301",
//             "53033011801",
//             "53033011901",
//             "53033000404",
//             "53033000101",
//             "53033000102",
//             "53033000201",
//             "53033000202",
//             "53033000601",
//             "53033001100",
//             "53033001201",
//             "53033001202",
//             "53033002000",
//             "53033002100",
//             "53033002500",
//             "53033002600",
//             "53033003800",
//             "53033001300",
//             "53033001800",
//             "53033001900",
//             "53033002700",
//             "53033003601",
//             "53033003602",
//             "53033000300",
//             "53033000402",
//             "53033000403",
//             "53033000602",
//             "53033004600",
//             "53033003201",
//             "53033005600",
//             "53033005700",
//             "53033005803",
//             "53033005804",
//             "53033005901",
//             "53033005902",
//             "53033006800",
//             "53033006900",
//             "53033007001",
//             "53033007002",
//             "53033007101",
//             "53033007102",
//             "53033007201",
//             "53033007202",
//             "53033007301",
//             "53033007302",
//             "53033007303",
//             "53033007403",
//             "53033007405",
//             "53033007406",
//             "53033007501",
//             "53033007502",
//             "53033007503",
//             "53033007901",
//             "53033008002",
//             "53033008003",
//             "53033008004",
//             "53033008101",
//             "53033008102",
//             "53033008200",
//             "53033008300",
//             "53033008401",
//             "53033008402",
//             "53033008500",
//             "53033008600",
//             "53033008700",
//             "53033009000",
//             "53033009100",
//             "53033009200",
//             "53033009300",
//             "53033009400",
//             "53033009600",
//             "53033009701",
//             "53033009702",
//             "53033009801",
//             "53033009802",
//             "53033009900",
//             "53033010001",
//             "53033010002",
//             "53033010101",
//             "53033010302",
//             "53033010401",
//             "53033010402",
//             "53033010501",
//             "53033010502",
//             "53033010601",
//             "53033010602",
//             "53033010701",
//             "53033010702",
//             "53033010800",
//             "53033010900",
//             "53033011001",
//             "53033011002",
//             "53033011200",
//             "53033011300",
//             "53033011401",
//             "53033011402",
//             "53033011500",
//             "53033011601",
//             "53033011602",
//             "53033011700",
//             "53033012000",
//             "53033012100",
//             "53033026500",
//             "53033000500",
//             "53033001400",
//             "53033001500",
//             "53033001600",
//             "53033001701",
//             "53033001702",
//             "53033002800",
//             "53033002900",
//             "53033003000",
//             "53033003100",
//             "53033003202",
//             "53033003301",
//             "53033003302",
//             "53033003400",
//             "53033003500",
//             "53033004701",
//             "53033004702",
//             "53033004703",
//             "53033004800",
//             "53033004901",
//             "53033004902",
//             "53033005000",
//             "53033005100",
//             "53033005401",
//             "53033005402",
//             "53033005801",
//             "53033006000",
//             "53033006500",
//             "53033006600",
//             "53033006701",
//             "53033006702",
//             "53033006703",
//             "53033007203",
//             "53033007404",
//             "53033011101"
//         ]
//     },
//     {
//         "lod": "county",
//         "ids": [
//             "53033"
//         ]
//     },
//     {
//         "lod": "school",
//         "ids": [
//             "5303540",
//             "5307920",
//             "5308130",
//             "5307710",
//             "5307230"
//         ]
//     }
// ]

// Albany, NY
// const geoDetails = [
//     {
//         "lod": "tract",
//         "ids": [
//             "36001002100",
//             "36001000404",
//             "36001002500",
//             "36001000201",
//             "36001002300",
//             "36001000301",
//             "36001000100",
//             "36001000403",
//             "36001002200",
//             "36001001803",
//             "36001000202",
//             "36001000501",
//             "36001001600",
//             "36001001100",
//             "36001000302",
//             "36001000600",
//             "36001001802",
//             "36001000502",
//             "36001001500",
//             "36001001901",
//             "36001000800",
//             "36001001804",
//             "36001001700",
//             "36001002000",
//             "36001001400",
//             "36001002600",
//             "36001001902",
//             "36001000401",
//             "36001000700"
//         ]
//     },
//     {
//         "lod": "county",
//         "ids": [
//             "36001"
//         ]
//     },
//     {
//         "lod": "school",
//         "ids": [
//             "3602460"
//         ]
//     }
// ]